import { useState, useMemo, useEffect } from 'react';
import { refHandler } from 'app/utils/helper-functions';
import { AddComment } from 'app/components/editor/sidebar/comments/AddComment';
import { AddTag } from 'app/components/editor/sidebar/tags/AddTag';
import { StyledElement } from 'app/slate/components/StyledElement';
import { AddStyledParagraph } from 'app/components/editor/sidebar/styledparagrah/AddStyledParagraph';
import { centeredPopperElement } from 'app/utils/helper-functions';
import { usePopper } from 'react-popper';
import { Portal } from '../../components/editor/sidebar/Portal';
import { useEditorSelector } from 'app/state/contexts/EditorContext';
import { useEditorMode } from 'app/state/contexts/EditorContext';
import { useSlate } from 'slate-react';
import { Editor } from 'slate';
import { isInsideStyledParagraph } from '../extensions/styled-paragraph';
import { getClosestElementByType } from '../utils';

export const Paragraph = ({ element, attributes, children, withComments, withStyledParagrah, withTags }) => {
  const [ref, setRef] = useState(null);
  const [show, setShow] = useState(true);
  const selectionId = useEditorSelector((editor) => editor.selectionId);
  const { isTaggingMode } = useEditorMode();
  const editor = useSlate();
  const isInStyledParagraph = isInsideStyledParagraph(editor);

  const isMultipleParagraphsSelected = useMemo(() => {
    if (!editor.selection) return false;
    return (
      Array.from(
        Editor.nodes(editor, {
          at: editor.selection,
          match: (n) => Editor.isBlock(editor, n) && n.type === 'paragraph',
        })
      ).length > 1
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, editor.selection]);

  useEffect(() => {
    const closestParagraph = getClosestElementByType(isInStyledParagraph, 'paragraph');
    if (isInStyledParagraph && closestParagraph) {
      setShow(closestParagraph.uuid === element.uuid);
      return;
    }
    if (!selectionId) {
      setShow(false);
      return;
    }

    setShow(selectionId === element.uuid);
  }, [editor, editor.selection, element.uuid, isInStyledParagraph, selectionId]);
  const anchor = `anchor-${element.uuid}`;

  return (
    <div {...attributes} ref={refHandler(attributes, setRef)}>
      {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
      <a contentEditable="false" href={`#${anchor}`} id={anchor}></a>

      <SeletedTextWrapper popperRef={ref} show={show}>
        <>
          {withComments && !isMultipleParagraphsSelected && ref ? <AddComment element={element} /> : null}
          {withStyledParagrah && !isTaggingMode && ref ? <AddStyledParagraph isInsideStyledParagraph={isInStyledParagraph} /> : null}
          {withTags && isTaggingMode && ref ? <AddTag element={element} /> : null}
        </>
      </SeletedTextWrapper>

      <StyledElement element={element} attributes={attributes} children={children} />
    </div>
  );
};

export const SeletedTextWrapper = ({ children, popperRef, show }) => {
  const [popperElement, setPopperElement] = useState(null);
  const referenceElement = useMemo(() => centeredPopperElement(popperRef), [popperRef]);
  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'right-end',
  });
  return (
    <>
      {show && (
        <Portal>
          <div ref={setPopperElement} style={{ ...styles.popper, gap: '2px', display: 'flex', flexDirection: 'column' }} {...attributes.popper}>
            {children}
          </div>
        </Portal>
      )}
    </>
  );
};
